/* eslint-disable react-refresh/only-export-components */
/**
 * Toast — Lightweight notification system for error/success feedback.
 *
 * Usage:
 *   import { useToast, ToastProvider } from './Toast';
 *
 *   // In App.jsx: wrap with <ToastProvider>
 *   // In any component:
 *   const toast = useToast();
 *   toast.error('Failed to save changes');
 *   toast.success('Assignment created');
 *   toast.info('Refreshing data...');
 */

import { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react';

const ToastContext = createContext(null);

export function useToast() {
  const ctx = useContext(ToastContext);
  if (!ctx) {
    // Fallback for components outside provider — log to console
    return {
      error: (msg) => console.error('[Toast]', msg),
      success: (msg) => console.log('[Toast]', msg),
      info: (msg) => console.log('[Toast]', msg),
    };
  }
  return ctx;
}

const TOAST_DURATION = { error: 6000, success: 3000, info: 4000 };
const TOAST_COLORS = {
  error: { bg: '#dc2626', border: '#ef4444' },
  success: { bg: '#059669', border: '#10b981' },
  info: { bg: '#2563eb', border: '#3b82f6' },
};

let toastId = 0;

export function ToastProvider({ children }) {
  const [toasts, setToasts] = useState([]);
  const timersRef = useRef({});

  const dismiss = useCallback((id) => {
    clearTimeout(timersRef.current[id]);
    delete timersRef.current[id];
    setToasts(prev => prev.filter(t => t.id !== id));
  }, []);

  const addToast = useCallback((type, message) => {
    const id = ++toastId;
    setToasts(prev => [...prev.slice(-4), { id, type, message }]); // Max 5 visible
    timersRef.current[id] = setTimeout(() => dismiss(id), TOAST_DURATION[type] || 4000);
    return id;
  }, [dismiss]);

  // Listen for global error events (from unhandled rejection handler)
  useEffect(() => {
    const handler = (e) => addToast('error', e.detail?.message || 'An unexpected error occurred.');
    window.addEventListener('juuni:error', handler);
    // Capture the ref at effect-run time — cleanup runs later, by which
    // point timersRef.current may have been mutated by scheduled toasts.
    const timers = timersRef.current;
    return () => {
      window.removeEventListener('juuni:error', handler);
      Object.values(timers).forEach(clearTimeout);
    };
  }, [addToast]);

  const api = {
    error: (msg) => addToast('error', msg),
    success: (msg) => addToast('success', msg),
    info: (msg) => addToast('info', msg),
    dismiss,
  };

  return (
    <ToastContext.Provider value={api}>
      {children}
      {/* Toast container — fixed bottom-right */}
      {toasts.length > 0 && (
        <div style={{
          position: 'fixed', bottom: 16, right: 16, zIndex: 99998,
          display: 'flex', flexDirection: 'column', gap: 8,
          pointerEvents: 'none',
        }}>
          {toasts.map(t => {
            const colors = TOAST_COLORS[t.type] || TOAST_COLORS.info;
            return (
              <div key={t.id} style={{
                pointerEvents: 'auto',
                background: colors.bg, borderLeft: `3px solid ${colors.border}`,
                color: '#fff', padding: '10px 16px', borderRadius: 6,
                fontSize: 11, fontFamily: 'var(--font-mono, monospace)',
                maxWidth: 360, boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
                display: 'flex', alignItems: 'center', gap: 10,
                animation: 'toastSlideIn 0.2s ease-out',
              }}>
                <span style={{ flex: 1 }}>{t.message}</span>
                <button onClick={() => dismiss(t.id)} style={{
                  background: 'none', border: 'none', color: 'rgba(255,255,255,0.7)',
                  cursor: 'pointer', fontSize: 14, padding: 0, lineHeight: 1,
                }}>×</button>
              </div>
            );
          })}
        </div>
      )}
      {/* Keyframe animation */}
      <style>{`
        @keyframes toastSlideIn {
          from { transform: translateX(100%); opacity: 0; }
          to { transform: translateX(0); opacity: 1; }
        }
      `}</style>
    </ToastContext.Provider>
  );
}
